import {Box, Divider, Paper} from '@mui/material';
import React, {useEffect, useRef, useState} from 'react';
import {useDrag, useDrop} from 'react-dnd';
import AurIcon, {IconNames} from '../../../common/aur-icon/AurIcon';
import AurButton from '../../../common/aur-button/AurButton';
import {ReactComponent as DragHandleOutlinedIcon} from './../../../../assests/svg/drag-handle-outlined.svg';
import {t} from 'i18next';
import AurPopup from '../../../common/aur-popup/AurPopup';

const ItemType = 'TAG';

export interface ITag {
  tagValueId: number;
  tagValue: string;
  displayOrder: number;
  isDeleted?: boolean;
}

interface DraggableTagProps {
  tag: ITag;
  index: number;
  isOrderingTag: boolean;
  moveTag?: (fromIndex: number, toIndex: number) => void;
  handleDelete: (id: number) => void;
  handleEdit: (tag: ITag) => void;
  canEditTags?: boolean;
}

const DraggableTag: React.FC<DraggableTagProps> = ({
  tag,
  isOrderingTag,
  index,
  moveTag,
  handleDelete,
  handleEdit,
  canEditTags = true
}) => {
  const [isEditingOrder, setIsEditingOrder] = useState<boolean>(isOrderingTag);
  const ref = useRef<HTMLDivElement>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  useEffect(() => {
    setIsEditingOrder(isOrderingTag);
  }, [isOrderingTag]);

  const [, drop] = useDrop({
    accept: ItemType,
    hover(item: {index: number}) {
      if (item.index !== index) {
        moveTag?.(item.index, index);
        item.index = index;
      }
    },
  });

  const [, drag] = useDrag({
    type: ItemType,
    item: {index},
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  if (isEditingOrder) {
    drag(drop(ref));
  }

  return (
    <>
      <Paper
        ref={ref}
        className={isEditingOrder ? 'drag-item' : 'drag-item disable-drag-item'}
        data-testid={tag.tagValueId}
      >
        <Box display="flex" alignItems="center" gap={1} tabIndex={0}>
          {isEditingOrder ? <DragHandleOutlinedIcon /> : null}
          {tag.tagValue}
        </Box>
        <Box className={'action-button'}>
          {!isEditingOrder && canEditTags ? (
            <>
              <AurButton
                onClick={() => handleEdit(tag)}
                type="secondary"
                classNames="permission-group-title-buttons"
                ariaLabel={t('fieldManagement.customField.renameTagHint')}
                dataTestId={`tag-edit-${tag.tagValueId}`}
              >
                <AurIcon icon={IconNames.Edit} />
              </AurButton>
              <AurButton
                onClick={() => setIsDeleteModalOpen(true)}
                type="secondary"
                classNames="permission-group-title-buttons"
                ariaLabel={t('fieldManagement.customField.deleteTagHint')}
                dataTestId={`tag-delete-${tag.tagValueId}`}
              >
                <AurIcon icon={IconNames.Remove} />
              </AurButton>
            </>
          ) : (
            <div className="permission-group-title-buttons" />
          )}
        </Box>
      </Paper>
      <Divider />
      {isDeleteModalOpen && canEditTags && (
        <AurPopup
          open={isDeleteModalOpen}
          className="aur-unsaved-changes-popup duplicate-tag-confirmation-popup"
          popupContent={t('ecaDocList.bulkTagging.deleteTag.content')}
          popupHeading={t('ecaDocList.bulkTagging.deleteTag.heading', {
            tagName: tag.tagValue,
          })}
          showPopupHeading={true}
          labelActionCancel={t('ecaDocList.bulkTagging.deleteTag.cancel')}
          labelActionOk={t('ecaDocList.bulkTagging.deleteTag.delete')}
          onCancel={() => setIsDeleteModalOpen(false)}
          onOk={() => handleDelete(tag.tagValueId)}
          cancelButtonTitle={t('ecaDocList.bulkTagging.deleteTag.cancelDelete')}
        ></AurPopup>
      )}
    </>
  );
};

export {DraggableTag};
